/**
 * Public verification page: /verify/:guid
 * Renders the credential under BOTH open standards it satisfies:
 *   - Open Badges 2.0 (hosted assertion, served from the issuer's record), and
 *   - Open Badges 3.0 / W3C Verifiable Credential (Ed25519-signed VC-JWT,
 *     did:web:api.badges.ninja, Bitstring Status List revocation).
 * Plus a "verify the signature yourself" explainer and every machine-readable
 * format a verifier/wallet/registry needs. Independent of AwardView — that page
 * is for celebration; this page is for skeptics.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import { useFormatDate } from '../../hooks/useFormatDate';
import { QRCode } from '../../components/QRCode';
import { fireAwardEvent } from '../../lib/awardEvents';

interface Award {
  id: string;
  image: string;
  issuedOn: string;
  expires?: string;
  recipient: { name: string; identity?: string; salt?: string; hashed?: boolean };
  badge: {
    id?: string;
    name: string;
    description: string;
    image: string;
    issuer: { id?: string; name: string; url: string; email: string; linkedinOrganizationId?: string; verified?: boolean };
  };
  revoked?: boolean;
  revocationReason?: string;
}

/** The OB3 fields we read for the crypto-proof card (fetched best-effort). */
interface Ob3Info {
  issuerDid?: string;
  statusListIndex?: string;
  statusListCredential?: string;
  keyId?: string;
}

function linkedinAddToProfileUrl(award: Award, guid: string, verifyUrl: string): string | null {
  const orgId = award.badge.issuer.linkedinOrganizationId;
  const orgName = award.badge.issuer.name;
  // Show the button whenever we have at least an issuer name. organizationId
  // is preferred — when present and valid, LinkedIn pre-fills the cert with
  // the verified Company Page. organizationName is a required fallback when
  // the issuer isn't on LinkedIn AND a sanity-check display value when an ID
  // is set but doesn't resolve to the expected page.
  if (!orgId && !orgName) return null;
  const issued = new Date(award.issuedOn);
  const params = new URLSearchParams({
    startTask: 'CERTIFICATION_NAME',
    name: award.badge.name,
    issueYear: String(issued.getFullYear()),
    issueMonth: String(issued.getMonth() + 1),
    certUrl: verifyUrl,
    certId: guid,
  });
  if (orgId)   params.set('organizationId', orgId);
  if (orgName) params.set('organizationName', orgName);
  if (award.expires) {
    const exp = new Date(award.expires);
    params.set('expirationYear', String(exp.getFullYear()));
    params.set('expirationMonth', String(exp.getMonth() + 1));
  }
  return `https://www.linkedin.com/profile/add?${params.toString()}`;
}

const API_URL = import.meta.env.VITE_CERTIFY_BASE_URL || import.meta.env.VITE_API_URL;
const ISSUER_DID_FALLBACK = 'did:web:api.badges.ninja';

export function AwardVerify() {
  const { guid } = useParams<{ guid: string }>();
  const { t } = useTranslation('common');
  const formatDate = useFormatDate();
  const [award, setAward] = useState<Award | null>(null);
  const [ob3, setOb3] = useState<Ob3Info>({});
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!guid) return;
    let cancelled = false;
    const MAX_ATTEMPTS = 3;

    const load = async (attempt: number): Promise<void> => {
      try {
        const r = await fetch(`${API_URL}/certify-badge/award/${guid}`);
        // A real 404 is terminal — the credential genuinely doesn't exist / was deleted.
        if (r.status === 404) {
          if (!cancelled) { setError(t('award.verify.notFoundDeleted')); setLoading(false); }
          return;
        }
        // Any other non-OK (5xx/transient) is retryable — a verifier must not be
        // told a valid credential is "not found" over a transient blip.
        if (!r.ok) throw new Error(`status ${r.status}`);
        const data = await r.json();
        if (cancelled) return;
        setAward(data);
        setLoading(false);
      } catch {
        if (cancelled) return;
        if (attempt < MAX_ATTEMPTS) {
          setTimeout(() => { if (!cancelled) void load(attempt + 1); }, 600 * attempt);
          return;
        }
        setError(t('award.verify.unavailable', 'Verification is temporarily unavailable. Please refresh in a moment.'));
        setLoading(false);
      }
    };

    void load(1);
    return () => { cancelled = true; };
  }, [guid, t]);

  // Best-effort OB3 enrichment: the signed VC carries the real status-list
  // index + issuer DID, and the DID document carries the verification key id.
  // Both are enhancement-only — a failure never blocks the page (the standards,
  // explainer and format links are all derivable from the guid alone).
  useEffect(() => {
    if (!guid) return;
    let cancelled = false;
    (async () => {
      try {
        const [vcRes, didRes] = await Promise.allSettled([
          fetch(`${API_URL}/certify-badge/award/${guid}/vc?format=json`),
          fetch(`${API_URL}/.well-known/did.json`),
        ]);
        const next: Ob3Info = {};
        if (vcRes.status === 'fulfilled' && vcRes.value.ok) {
          const vc = await vcRes.value.json();
          next.issuerDid = vc?.issuer?.id;
          next.statusListIndex = vc?.credentialStatus?.statusListIndex;
          next.statusListCredential = vc?.credentialStatus?.statusListCredential;
        }
        if (didRes.status === 'fulfilled' && didRes.value.ok) {
          const did = await didRes.value.json();
          next.keyId = did?.verificationMethod?.[0]?.id;
        }
        if (!cancelled) setOb3(next);
      } catch { /* enrichment is optional */ }
    })();
    return () => { cancelled = true; };
  }, [guid]);

  if (loading) return (
    <div className="flex h-screen items-center justify-center bg-gray-50">
      <div className="h-8 w-8 rounded-full border-4 border-primary-200 border-t-primary-600 animate-spin" />
    </div>
  );

  if (error || !award) return (
    <div className="flex h-screen items-center justify-center bg-gray-50">
      <div className="text-center">
        <h1 className="text-2xl font-bold text-gray-800 mb-2">{t('award.verify.failed')}</h1>
        <p className="text-gray-500 mb-4">{error}</p>
        <a href="/" className="text-primary-600 hover:underline">{t('award.verify.goHome')}</a>
      </div>
    </div>
  );

  const isExpired = award.expires && new Date(award.expires) < new Date();
  const revoked = !!award.revoked;
  const verifyUrl = window.location.href;
  const liUrl = linkedinAddToProfileUrl(award, guid!, verifyUrl);
  const issuerDid = ob3.issuerDid || ISSUER_DID_FALLBACK;

  // Machine-readable formats (all derivable from the guid).
  const ob2JsonUrl = award.id;
  const ob3JsonUrl = `${API_URL}/certify-badge/award/${guid}/vc?format=json`;
  const vcJwtUrl = `${API_URL}/certify-badge/award/${guid}/vc`;
  const didUrl = `${API_URL}/.well-known/did.json`;
  const jwksUrl = `${API_URL}/.well-known/jwks.json`;

  function copyLink() {
    navigator.clipboard?.writeText(verifyUrl).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  }

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'EducationalOccupationalCredential',
    name: award.badge.name,
    description: award.badge.description,
    identifier: award.id,
    url: verifyUrl,
    image: award.image || award.badge.image,
    dateCreated: award.issuedOn,
    ...(award.expires ? { validUntil: award.expires } : {}),
    credentialCategory: 'Badge',
    recognizedBy: {
      '@type': 'Organization',
      name: award.badge.issuer.name,
      url: award.badge.issuer.url,
    },
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd).replace(/</g, '\\u003c') }}
      />
      <div className="max-w-3xl mx-auto space-y-6">
        {/* Hero */}
        <div className="bg-white rounded-2xl shadow p-6 text-center">
          {revoked ? (
            <div className="inline-flex items-center gap-2 rounded-full bg-red-50 border border-red-200 px-3 py-1 text-xs font-medium text-red-700 mb-4">
              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><circle cx="12" cy="12" r="9" /><path d="M15 9l-6 6M9 9l6 6" /></svg>
              {t('award.verify.revoked', 'Revoked credential')}
            </div>
          ) : (
            <div className="inline-flex items-center gap-2 rounded-full bg-emerald-50 border border-emerald-200 px-3 py-1 text-xs font-medium text-emerald-700 mb-4">
              <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z" clipRule="evenodd" /></svg>
              {t('award.verify.genuine')}
            </div>
          )}
          <img src={award.image || award.badge.image} alt={award.badge.name} className={`h-32 w-32 mx-auto object-contain mb-3 ${revoked ? 'opacity-50 grayscale' : ''}`} />
          <h1 className="text-xl font-bold text-gray-900">{award.badge.name}</h1>
          <p className="text-sm text-gray-500 mt-1">
            {t('award.verify.issuedBy')}{' '}
            <a href={award.badge.issuer.url} target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline">
              {award.badge.issuer.name}
            </a>
            {award.badge.issuer.verified && (
              <span className="inline-flex items-center gap-1 align-middle ml-1.5 rounded-full bg-emerald-50 border border-emerald-200 px-2 py-0.5 text-[11px] font-medium text-emerald-700">
                <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z" clipRule="evenodd" /></svg>
                {t('award.verify.issuerVerified')}
              </span>
            )}
            {' '}{t('award.verify.issuedTo')} <span className="font-medium text-gray-800">{award.recipient.name}</span>
          </p>
          <p className="text-xs text-gray-400 mt-1">{t('award.verify.on')} {formatDate(award.issuedOn, 'long')}</p>
          {award.expires && !isExpired && !revoked && (
            <p className="text-xs text-gray-400 mt-1">{t('award.verify.validUntil', { date: formatDate(award.expires, 'long') })}</p>
          )}
          <button
            type="button"
            onClick={copyLink}
            className="mt-3 inline-flex items-center gap-2 rounded-full border border-gray-200 bg-gray-50 px-3 py-1 text-xs font-medium text-gray-600 hover:bg-gray-100"
          >
            <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M10 13a5 5 0 007.5.5l3-3A5 5 0 0013.5 3.5l-1.7 1.7" />
              <path d="M14 11a5 5 0 00-7.5-.5l-3 3A5 5 0 0010.5 20.5l1.7-1.7" />
            </svg>
            {copied ? t('award.share.copied', 'Copied!') : t('award.share.copyLink', 'Copy link')}
          </button>
          {!revoked && liUrl && (
            <a href={liUrl} target="_blank" rel="noopener noreferrer"
              onClick={() => fireAwardEvent(guid!, 'click_linkedin_addtoprofile')}
              className="mt-4 inline-flex items-center gap-2 px-[18px] py-[11px] rounded-xl text-sm font-semibold text-white bg-[#0a66c2] hover:bg-[#004182] shadow-sm transition hover:-translate-y-0.5">
              <svg className="h-[17px] w-[17px]" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.063 2.063 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" /></svg>
              {t('award.view.addToLinkedIn', 'Add to LinkedIn profile')}
            </a>
          )}
          {isExpired && !revoked && (
            <p className="mt-3 inline-block rounded-lg bg-red-50 border border-red-200 px-3 py-1 text-xs text-red-700">
              {t('award.verify.expiredOn', { date: formatDate(award.expires!, 'long') })}
            </p>
          )}
          {revoked && (
            <p className="mt-3 inline-block rounded-lg bg-red-50 border border-red-200 px-3 py-1.5 text-xs text-red-700 max-w-md">
              {t('award.verify.revokedNotice', 'This credential has been revoked by the issuer and is no longer valid.')}
              {award.revocationReason ? ` ${t('award.verify.revokedReason', 'Reason')}: ${award.revocationReason}` : ''}
            </p>
          )}
        </div>

        {/* Credential standards — valid under OB 2.0 (hosted) AND OB 3.0 (signed VC) */}
        <div className="bg-white rounded-2xl shadow p-6">
          <h2 className="text-base font-semibold text-gray-900 mb-1">{t('award.verify.standards.title', 'Credential standards')}</h2>
          <p className="text-xs text-gray-500 mb-4">{t('award.verify.standards.subtitle', 'This credential is valid under two open standards at once.')}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <StandardCard
              name={t('award.verify.standards.ob2name', 'Open Badges 2.0')}
              mode={t('award.verify.standards.ob2mode', 'Hosted verification')}
              desc={t('award.verify.standards.ob2desc', 'Verified by fetching this record from badges.ninja. Universally supported, including Credly & Badgr imports.')}
            />
            <StandardCard
              highlight
              name={t('award.verify.standards.ob3name', 'Open Badges 3.0')}
              mode={t('award.verify.standards.ob3mode', 'Signed W3C Verifiable Credential')}
              desc={t('award.verify.standards.ob3desc', 'Cryptographically signed with an Ed25519 key. Verifiable by anyone, even offline, with no call back to our servers.')}
            />
          </div>
        </div>

        {/* Cryptographic proof — don't trust us, check the signature */}
        <div className="bg-white rounded-2xl shadow p-6">
          <h2 className="text-base font-semibold text-gray-900 mb-1">{t('award.verify.crypto.title', 'Cryptographic proof')}</h2>
          <p className="text-xs text-gray-500 mb-4">{t('award.verify.crypto.subtitle', 'You don’t have to trust us — check the signature yourself.')}</p>
          <div className="space-y-2 text-xs mb-5">
            <Row label={t('award.verify.crypto.issuerDid', 'Issuer (DID)')} value={issuerDid} mono />
            <Row label={t('award.verify.crypto.signature', 'Signature')} value="Ed25519 · EdDSA (VC-JWT)" />
            {ob3.keyId && <Row label={t('award.verify.crypto.keyId', 'Key ID')} value={ob3.keyId} mono />}
            <div className="grid grid-cols-[140px_1fr] gap-3 py-1">
              <div className="text-gray-500">{t('award.verify.crypto.revocation', 'Revocation')}</div>
              <div className="text-gray-700">
                {t('award.verify.crypto.statusListLabel', 'W3C Bitstring Status List')}
                {ob3.statusListIndex ? <> · <span className="font-mono">#{ob3.statusListIndex}</span></> : null}
                {ob3.statusListCredential && (
                  <> · <a href={ob3.statusListCredential} target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline">{t('award.verify.crypto.statusListLink', 'status list')} ↗</a></>
                )}
              </div>
            </div>
          </div>
          <ol className="space-y-2.5">
            <Step n={1}>{t('award.verify.crypto.step1', 'Download the signed VC-JWT below, or scan it into any Open Badges 3.0 wallet.')}</Step>
            <Step n={2}>{t('award.verify.crypto.step2', 'Resolve did:web:api.badges.ninja to fetch our public Ed25519 key from the DID document.')}</Step>
            <Step n={3}>{t('award.verify.crypto.step3', 'Verify the signature — if it checks out, the credential is authentic and unaltered, proven by math rather than by our word.')}</Step>
            <Step n={4}>{t('award.verify.crypto.step4', 'Check the status-list entry to confirm the issuer hasn’t revoked it.')}</Step>
          </ol>
          {!revoked ? (
            <div className="mt-4 inline-flex items-center gap-2 rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-1.5 text-xs font-medium text-emerald-700">
              <span className="relative flex h-2 w-2"><span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60" /><span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" /></span>
              {t('award.verify.crypto.statusOk', 'Signed credential · not revoked')}
            </div>
          ) : (
            <div className="mt-4 inline-flex items-center gap-2 rounded-lg bg-red-50 border border-red-200 px-3 py-1.5 text-xs font-medium text-red-700">
              {t('award.verify.crypto.statusRevoked', 'This credential has been revoked by the issuer.')}
            </div>
          )}
        </div>

        {/* What this proves */}
        <div className="bg-white rounded-2xl shadow p-6">
          <h2 className="text-base font-semibold text-gray-900 mb-3">{t('award.verify.proves.title')}</h2>
          <ul className="space-y-2 text-sm text-gray-700">
            <li className="flex items-start gap-2">
              <Check />
              <Trans
                t={t}
                i18nKey="award.verify.proves.hosted"
                components={{ host: <span className="font-mono text-xs" /> }}
              >
                <span>This page is hosted on <span className="font-mono text-xs">badges.ninja</span> and served from the issuer's record.</span>
              </Trans>
            </li>
            <li className="flex items-start gap-2">
              <Check />
              <Trans
                t={t}
                i18nKey="award.verify.proves.openBadges"
                components={{
                  a: <a href="https://www.imsglobal.org/spec/ob/v2p0/" target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline" />,
                  b: <a href="https://www.imsglobal.org/spec/ob/v3p0/" target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline" />,
                }}
              >
                <span>The badge follows the <a href="https://www.imsglobal.org/spec/ob/v2p0/" target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline">Open Badges 2.0</a> and <b>3.0</b> specifications.</span>
              </Trans>
            </li>
            <li className="flex items-start gap-2">
              <Check />
              <span>{t('award.verify.proves.identity')}</span>
            </li>
            <li className="flex items-start gap-2">
              <Check />
              <span>{t('award.verify.proves.signed', 'The 3.0 credential is digitally signed, so it stays verifiable even if downloaded, forwarded, or checked offline.')}</span>
            </li>
          </ul>
        </div>

        {/* Identity */}
        <div className="bg-white rounded-2xl shadow p-6">
          <h2 className="text-base font-semibold text-gray-900 mb-3">{t('award.verify.identity.title')}</h2>
          <div className="space-y-2 text-xs">
            <Row label={t('award.verify.identity.awardId')} value={award.id} mono />
            <Row label={t('award.verify.identity.badgeId')} value={award.badge.id || '—'} mono />
            {award.recipient.identity && (
              <Row label={t('award.verify.identity.recipientHash')} value={award.recipient.identity} mono />
            )}
            {award.recipient.salt && (
              <Row label={t('award.verify.identity.salt')} value={award.recipient.salt} mono />
            )}
            <Row label={t('award.verify.identity.recipientName')} value={award.recipient.name} />
            <Row label={t('award.verify.identity.issuer')} value={award.badge.issuer.name} />
            <Row label={t('award.verify.identity.issuerEmail')} value={award.badge.issuer.email} />
            <Row label={t('award.verify.identity.issuerDid', 'Issuer DID')} value={issuerDid} mono />
            {ob3.statusListIndex && (
              <Row label={t('award.verify.identity.statusIndex', 'Status list index')} value={ob3.statusListIndex} mono />
            )}
          </div>
        </div>

        {/* Machine-readable formats & keys */}
        <div className="bg-white rounded-2xl shadow p-6">
          <h2 className="text-base font-semibold text-gray-900 mb-1">{t('award.verify.formats.title', 'Machine-readable formats & keys')}</h2>
          <p className="text-xs text-gray-500 mb-4">{t('award.verify.formats.subtitle', 'For verifiers, wallets and registries.')}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            <FormatLink href={ob2JsonUrl} onClick={() => fireAwardEvent(guid!, 'click_verify')}
              title={t('award.verify.formats.ob2json', 'Open Badges 2.0 JSON')} hint="/certify-badge/award/{id}" icon="doc" />
            <FormatLink href={ob3JsonUrl} onClick={() => fireAwardEvent(guid!, 'click_verify')}
              title={t('award.verify.formats.ob3json', 'Open Badges 3.0 JSON')} hint="/vc?format=json" icon="shield" />
            <FormatLink href={vcJwtUrl} onClick={() => fireAwardEvent(guid!, 'click_verify')}
              title={t('award.verify.formats.vcjwt', 'Signed VC-JWT')} hint={t('award.verify.formats.vcjwtHint', 'Import to an OB3 wallet · EdDSA')} icon="shield" />
            <FormatLink href={didUrl} onClick={() => fireAwardEvent(guid!, 'click_verify')}
              title={t('award.verify.formats.did', 'DID document')} hint="/.well-known/did.json" icon="globe" />
            <FormatLink href={jwksUrl} onClick={() => fireAwardEvent(guid!, 'click_verify')}
              title={t('award.verify.formats.jwks', 'Public key set (JWKS)')} hint="/.well-known/jwks.json" icon="key" />
            {ob3.statusListCredential && (
              <FormatLink href={ob3.statusListCredential} onClick={() => fireAwardEvent(guid!, 'click_verify')}
                title={t('award.verify.formats.statusList', 'Revocation status list')} hint="/certify-badge/status/{id}" icon="check" />
            )}
          </div>
        </div>

        {/* QR + raw JSON link */}
        <div className="bg-white rounded-2xl shadow p-6 grid grid-cols-1 md:grid-cols-[160px_1fr] gap-4 items-start">
          <QRCode value={verifyUrl} size={160} />
          <div>
            <h2 className="text-base font-semibold text-gray-900 mb-2">{t('award.verify.offline.title')}</h2>
            <p className="text-sm text-gray-600 mb-3">{t('award.verify.offline.scanQR')}</p>
            <a href={ob2JsonUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-primary-600 hover:underline">
              {t('award.verify.offline.viewRaw')} ↗
            </a>
            <div className="mt-3">
              <Link to={`/awards/${guid}`} className="text-xs text-gray-500 hover:underline">
                {t('award.verify.offline.viewRecipient')} →
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function StandardCard({ name, mode, desc, highlight }: { name: string; mode: string; desc: string; highlight?: boolean }) {
  return (
    <div className={`rounded-xl border p-4 flex flex-col gap-1.5 ${highlight ? 'border-primary-200 bg-primary-50/60' : 'border-gray-200 bg-gray-50'}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="font-bold text-sm text-gray-900">{name}</span>
        <svg className="h-4 w-4 text-emerald-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z" clipRule="evenodd" /></svg>
      </div>
      <div className="text-[11.5px] text-gray-600">{mode}</div>
      <div className="text-[11.5px] text-gray-500 leading-snug">{desc}</div>
    </div>
  );
}

function Step({ n, children }: { n: number; children: ReactNode }) {
  return (
    <li className="flex items-start gap-3 text-[13px] text-gray-600">
      <span className="flex-none grid place-items-center h-[21px] w-[21px] rounded-full bg-primary-50 border border-primary-200 text-primary-700 text-[11px] font-bold mt-0.5">{n}</span>
      <span>{children}</span>
    </li>
  );
}

const FORMAT_ICONS: Record<string, ReactNode> = {
  doc: <path d="M4 7V4h16v3M9 20h6M12 4v16" />,
  shield: <><path d="M12 2l7 3v6c0 5-3.5 8-7 9-3.5-1-7-4-7-9V5z" /><path d="M9 12l2 2 4-4" /></>,
  globe: <><circle cx="12" cy="12" r="9" /><path d="M12 3a15 15 0 010 18M3 12h18" /></>,
  key: <path d="M15 7a4 4 0 11-5.66 5.66l-5.17 5.17a2 2 0 000 2.83a2 2 0 002.83 0L18 13.66A4 4 0 0015 7z" />,
  check: <><path d="M9 11l3 3L22 4" /><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" /></>,
};

function FormatLink({ href, title, hint, icon, onClick }: { href: string; title: string; hint: string; icon: string; onClick?: () => void }) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" onClick={onClick}
      className="flex items-start gap-3 rounded-xl border border-gray-200 bg-white p-3 hover:border-primary-200 transition">
      <span className="flex-none grid place-items-center h-8 w-8 rounded-lg bg-gray-50 text-primary-600">
        <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">{FORMAT_ICONS[icon]}</svg>
      </span>
      <span className="min-w-0">
        <span className="block text-[13px] font-semibold text-gray-800">{title}</span>
        <span className="block text-[11px] text-gray-400 font-mono break-all leading-tight mt-0.5">{hint}</span>
      </span>
    </a>
  );
}

function Check() {
  return (
    <svg className="h-4 w-4 text-emerald-500 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z" clipRule="evenodd" />
    </svg>
  );
}

function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="grid grid-cols-[140px_1fr] gap-3 py-1 border-b border-gray-50 last:border-0">
      <div className="text-gray-500">{label}</div>
      <div className={mono ? 'text-gray-700 font-mono break-all' : 'text-gray-700'}>{value}</div>
    </div>
  );
}
